import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, RefreshCw, Plus } from 'lucide-react';
import {
  BrioCard,
  BrioButton,
  BrioInput,
  BrioSelect,
} from '../../components/BrioComponents';
import { ToolbarIcon } from '../components/ToolbarIcon';
import ZoneTree from '../components/ZoneTree';
import CreateZoneModal from '../components/CreateZoneModal';
import StatusPill from '../components/StatusPill';
import { labelForNode } from '../utils/projectMeta';

// ---------------------------------------------------------------------------
// BOQ details form — the editable project sidecar fields (scheme stage, city,
// areas). Code, name, typology and briotime status are owned by the main app
// and shown read-only here. Replaces the old EditBoqDetailsModal.
// ---------------------------------------------------------------------------
function BoqDetailsForm({ project, taxonomies, onSave }) {
  const [form, setForm] = useState({
    schemeStage: project.schemeStage || '',
    city: project.city || '',
    builtUpArea: project.builtUpArea ?? '',
    carpetArea: project.carpetArea ?? '',
    constructionArea: project.constructionArea ?? '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  // Re-sync the form when a different project is selected. The useState
  // initializer above only runs on mount, so without this the form would keep
  // showing the previously selected project's BOQ details when switching.
  useEffect(() => {
    setForm({
      schemeStage: project.schemeStage || '',
      city: project.city || '',
      builtUpArea: project.builtUpArea ?? '',
      carpetArea: project.carpetArea ?? '',
      constructionArea: project.constructionArea ?? '',
    });
    setError(null);
    setSaved(false);
  }, [
    project.projectCode,
    project.schemeStage,
    project.city,
    project.builtUpArea,
    project.carpetArea,
    project.constructionArea,
  ]);

  const specDepthNodes = useMemo(
    () => taxonomies.find((t) => t.code === 'spec_depth')?.nodes || [],
    [taxonomies]
  );

  const update = (field) => (e) => {
    setSaved(false);
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const result = await onSave({
      projectCode: project.projectCode,
      name: project.name,
      typology: project.category,
      schemeStage: form.schemeStage || null,
      city: form.city,
      builtUpArea: form.builtUpArea,
      carpetArea: form.carpetArea,
      constructionArea: form.constructionArea,
    });
    setSubmitting(false);
    if (result.success) {
      setSaved(true);
    } else {
      setError(result.error || 'Failed to save BOQ details.');
    }
  };

  const field = (label, hint, input) => (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.3rem' }}>{label}</label>
      {input}
      {hint && <div style={{ fontSize: '0.76rem', color: 'var(--brio-text-muted)', marginTop: '0.25rem' }}>{hint}</div>}
    </div>
  );

  return (
    <BrioCard>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0, fontFamily: 'Raleway, sans-serif', fontSize: '1.05rem' }}>BOQ details</h3>
        <span style={{ fontSize: '0.78rem', color: 'var(--brio-text-muted)' }}>
          Code, name, typology &amp; status are managed in the main app.
        </span>
      </div>

      <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--brio-surface-muted, #f7f7f5)', borderRadius: 6, fontSize: '0.85rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <code style={{ fontSize: '0.78rem', color: 'var(--brio-text-muted)' }}>{project.projectCode}</code>
          <strong>{project.name}</strong>
          <StatusPill status={project.briotimeStatus} />
        </div>
        <div style={{ color: 'var(--brio-text-muted)', marginTop: '0.3rem' }}>
          Typology · {labelForNode(taxonomies.find((t) => t.code === 'typology'), project.category) || '—'}
        </div>
      </div>

      {error && (
        <div style={{ padding: '0.6rem 0.8rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '1rem', fontSize: '0.85rem' }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ maxWidth: 420 }}>
        {field('Scheme stage', 'Current design maturity for QS purposes.',
          <BrioSelect value={form.schemeStage} onChange={update('schemeStage')}>
            <option value="">— Not set —</option>
            {specDepthNodes.map((n) => (
              <option key={n.id} value={n.code}>{n.label}</option>
            ))}
          </BrioSelect>
        )}
        {field('City', 'Optional — used for rate filtering by location.',
          <BrioInput value={form.city} onChange={update('city')} placeholder="e.g. Coonoor" />
        )}
        {field('Built-up area (m²)', 'Optional — cost/area basis in summaries.',
          <BrioInput type="number" step="any" value={form.builtUpArea} onChange={update('builtUpArea')} placeholder="e.g. 820" />
        )}
        {field('Carpet area (m²)', 'Optional — cost/area basis in summaries.',
          <BrioInput type="number" step="any" value={form.carpetArea} onChange={update('carpetArea')} placeholder="e.g. 540" />
        )}
        {field('Construction area (m²)', 'Optional — cost/area basis in summaries.',
          <BrioInput type="number" step="any" value={form.constructionArea} onChange={update('constructionArea')} placeholder="e.g. 910" />
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem' }}>
          <BrioButton variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving…' : (project.hasMirror ? 'Save changes' : 'Set up BOQ')}
          </BrioButton>
          {saved && <span style={{ fontSize: '0.83rem', color: '#2e7d32' }}>Saved.</span>}
        </div>
      </form>
    </BrioCard>
  );
}

// ---------------------------------------------------------------------------
// Zones — moved here from the Bill of Quantities tab. Adding the first zone
// bootstraps the boq.projects mirror so the zone has a project_id to attach to.
// ---------------------------------------------------------------------------
function countZones(tree) {
  return (tree || []).reduce((n, z) => n + 1 + countZones(z.children), 0);
}

function ZonesSection({ project, ensureMirror, zonesApi }) {
  const { tree, loading, error, refresh, createZone, updateZone, deleteZone } = zonesApi;
  const [creatingFor, setCreatingFor] = useState(undefined); // undefined = closed, null = top-level, zone = under that zone
  const [editingZone, setEditingZone] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const zoneCount = countZones(tree);

  const handleOpenCreate = async (parentZone) => {
    if (!project.hasMirror) {
      setBootstrapping(true);
      const result = await ensureMirror();
      setBootstrapping(false);
      if (!result.success) return;
    }
    setCreatingFor(parentZone || null);
  };

  return (
    <BrioCard>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: collapsed ? 0 : '0.5rem' }}>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
        >
          {collapsed ? <ChevronRight size={16} style={{ color: 'var(--brio-text-muted)' }} /> : <ChevronDown size={16} style={{ color: 'var(--brio-text-muted)' }} />}
          <h3 style={{ margin: 0, fontFamily: 'Raleway, sans-serif', fontSize: '1.05rem' }}>
            Zones {zoneCount > 0 && <span style={{ color: 'var(--brio-text-muted)', fontWeight: 400, fontSize: '0.9rem' }}>· {zoneCount}</span>}
          </h3>
        </button>
        <div style={{ display: 'flex', gap: '0.15rem', alignItems: 'center' }}>
          <ToolbarIcon onClick={refresh} disabled={loading || !project.boqId} tooltip="Refresh">
            <RefreshCw size={15} />
          </ToolbarIcon>
          <BrioButton
            variant="primary"
            onClick={() => handleOpenCreate(null)}
            disabled={bootstrapping}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.7rem', fontSize: '0.82rem' }}
          >
            <Plus size={14} /> {bootstrapping ? 'Setting up…' : 'Zone'}
          </BrioButton>
        </div>
      </div>

      {collapsed ? null : !project.boqId ? (
        <div style={{ color: 'var(--brio-text-muted)', fontSize: '0.9rem', padding: '0.5rem 0' }}>
          Add the first zone above — BOQ will be enabled for this project automatically.
        </div>
      ) : loading ? (
        <div style={{ color: 'var(--brio-text-muted)', padding: '0.5rem 0' }}>Loading zones…</div>
      ) : error ? (
        <div style={{ color: '#a94442', padding: '0.5rem 0' }}>{error}</div>
      ) : (
        <ZoneTree
          tree={tree}
          onAddChild={(zone) => setCreatingFor(zone)}
          onEdit={(zone) => setEditingZone(zone)}
          onDelete={(zone) => deleteZone(zone.id)}
        />
      )}

      {creatingFor !== undefined && (
        <CreateZoneModal
          parent={creatingFor}
          onClose={() => setCreatingFor(undefined)}
          onSubmit={(input) => createZone({ ...input, parentId: input.parentId })}
        />
      )}
      {editingZone && (
        <CreateZoneModal
          zone={editingZone}
          onClose={() => setEditingZone(null)}
          onSubmit={(input) => updateZone(editingZone.id, input)}
        />
      )}
    </BrioCard>
  );
}

function ProjectDetailsPage({ project, taxonomies, zonesApi, ensureMirror, upsertBoqMirror }) {
  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <BoqDetailsForm
        project={project}
        taxonomies={taxonomies}
        onSave={(fields) => upsertBoqMirror(project.projectCode, fields)}
      />
      <ZonesSection project={project} ensureMirror={ensureMirror} zonesApi={zonesApi} />
    </div>
  );
}

export default ProjectDetailsPage;
